import * as Styled from './PunchupEventInfo.styles';

import { Copy, Layers2, Link, Loader2, Lock, Pencil, QrCode, Store } from 'lucide-react';
import { FC, memo, useState } from 'react';

import { EventInfoProps } from './types';
import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { ZERO_TIME } from 'utils/constants';
import { airbrake } from 'ErrorBoundary';
import { generatePath, useHistory } from 'react-router-dom';
import { getData } from 'services/api';
import { handleDownload } from 'utils/handleLink';
// import { useMutation } from '@tanstack/react-query';
// import { deleteEvent } from 'features/PunchupEditEvent/services';
// import { duplicateEvent } from '../EventInfo/services';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

enum ActionType {
	DUPLICATE = 'duplicate',
	EDIT = 'edit',
	DELETE = 'delete',
	DOWNLOAD_QR = 'download-qr',
	// Hidden for now (TIC-531): DOWNLOAD_SETTLEMENT_REPORT = 'download-settlement-report',
	COPY_EVENT_ID = 'copy-event-id',
	COPY_SHOW_URL = 'copy-show-url',
	BOX_OFFICE = 'box-office',
}

type ActionDef = { label: string; icon: JSX.Element; action: ActionType };

const PunchupEventInfo: FC<EventInfoProps> = ({
	address,
	date,
	endTime,
	eventTitle,
	onSaleDate,
	startTime,
	hasPassword,
}: EventInfoProps) => {
	const { push } = useHistory();
	const { t } = useTranslation();
	const { id: eventId }: IdParamsType = useParams();

	const [isLoadingAction, setIsLoadingAction] = useState<ActionType>();

	const actions: ActionDef[] = [
		{ label: 'Edit', icon: <Pencil size={18} />, action: ActionType.EDIT },
		{ label: 'Duplicate', icon: <Layers2 size={18} />, action: ActionType.DUPLICATE },
		{ label: 'Download QR', icon: <QrCode size={18} />, action: ActionType.DOWNLOAD_QR },
		// Hidden for now (TIC-531):
		// {
		// 	label: 'Download Settlement Report',
		// 	icon: <FileText size={18} />,
		// 	action: ActionType.DOWNLOAD_SETTLEMENT_REPORT,
		// },
		{ label: 'Copy Show ID', icon: <Copy size={18} />, action: ActionType.COPY_EVENT_ID },
		{ label: 'Copy Show URL', icon: <Link size={18} />, action: ActionType.COPY_SHOW_URL },
	];

	const boxOfficeAction: ActionDef = { label: 'Box Office', icon: <Store size={18} />, action: ActionType.BOX_OFFICE };

	// const { mutate: mutateDeleteEvent } = useMutation(deleteEvent, {
	// 	onSuccess: () => window.parent.postMessage({ action: 'event-deleted', eventId }, '*'),
	// 	onError: () => window.parent.postMessage({ action: 'event-delete-error', eventId }, '*'),
	// });

	// const { mutate: mutateDuplicateEvent } = useMutation(DuplicateEvent, {
	// 	onSuccess: ({ data }) => window.parent.postMessage({ action: 'event-duplicated', eventId: data.ID }, '*'),
	// 	onError: () => window.parent.postMessage({ action: 'event-duplicate-error', eventId }, '*'),
	// });

	const zeroTime = onSaleDate === ZERO_TIME;

	const onClickEdit = () => {
		window.parent.postMessage({ action: 'event-edit-clicked', eventId }, '*');
		push(ROUTES.EDIT_EVENT_PATH.replace(':id', eventId));
	};
	const onClickDuplicate = () => {
		window.parent.postMessage({ action: 'event-duplicated', eventId }, '*');
		// mutateDuplicateEvent({ eventId }, { onSettled: () => setIsLoadingAction(undefined) });
	};
	const onClickCopyEventId = () => {
		window.parent.postMessage({ action: 'event-id-copied', eventId }, '*');
	};
	const onClickCopyShowUrl = () => {
		window.parent.postMessage({ action: 'event-url-copied', eventId }, '*');
	};
	const onClickBoxOffice = () => push(generatePath(ROUTES.BOX_OFFICE, { id: eventId }));
	const onClickDownloadQR = async () => {
		setIsLoadingAction(ActionType.DOWNLOAD_QR);
		try {
			const response = await getData(`${process.env.REACT_APP_API_BASE}/public/ticketing/events/${eventId}/qr`, {
				responseType: 'blob',
				headers: { 'Content-Type': 'blob' },
			});
			handleDownload(response, `${eventTitle} QR.png`);
		} catch (error) {
			airbrake.notify({ error: 'Error downloading QR', context: { error } });
		} finally {
			setIsLoadingAction(undefined);
		}
	};
	// Hidden for now (TIC-531):
	// const onClickDownloadSettlementReport = async () => {
	// 	setIsLoadingAction(ActionType.DOWNLOAD_SETTLEMENT_REPORT);
	// 	try {
	// 		const response = await getData(`/events/${eventId}/settlement-report.pdf`, {
	// 			responseType: 'blob',
	// 			headers: { 'Content-Type': 'blob' },
	// 		});
	// 		handleDownload(response, `${eventTitle} Settlement Report.pdf`);
	// 	} catch (error) {
	// 		airbrake.notify({ error: 'Error downloading settlement report', context: { error } });
	// 	} finally {
	// 		setIsLoadingAction(undefined);
	// 	}
	// };

	const handleActionClick = (action: ActionType) => {
		switch (action) {
			case ActionType.DUPLICATE:
				onClickDuplicate();
				break;
			case ActionType.EDIT:
				onClickEdit();
				break;
			case ActionType.DOWNLOAD_QR:
				onClickDownloadQR();
				break;
			// Hidden for now (TIC-531):
			// case ActionType.DOWNLOAD_SETTLEMENT_REPORT:
			// 	onClickDownloadSettlementReport();
			// 	break;
			case ActionType.COPY_EVENT_ID:
				onClickCopyEventId();
				break;
			case ActionType.COPY_SHOW_URL:
				onClickCopyShowUrl();
				break;
			case ActionType.BOX_OFFICE:
				onClickBoxOffice();
				break;
		}
	};

	const renderActionButton = (action: ActionDef) => (
		<Styled.ActionButton
			key={action.action}
			onClick={() => handleActionClick(action.action)}
			disabled={action.action === isLoadingAction}
			title={action.label}
		>
			{isLoadingAction === action.action ? <Loader2 size={12} className="animate-spin" /> : action.icon}
		</Styled.ActionButton>
	);

	return (
		<Styled.Container>
			<Styled.InnerContainer>
				<Styled.InfoContainer>
					<Styled.Title>
						{eventTitle}
						{hasPassword && (
							<Lock size={24} style={{ marginLeft: '8px', display: 'inline-block', verticalAlign: 'middle' }} />
						)}
					</Styled.Title>
					<Styled.Text>
						{date} | {startTime} - {endTime}
					</Styled.Text>
					<Styled.Text>{address}</Styled.Text>

					{!zeroTime && <Styled.Text>{`${t('generic-onSale')} ${onSaleDate}`}</Styled.Text>}
				</Styled.InfoContainer>
			</Styled.InnerContainer>

			<Styled.ActionButtonsContainer>
				{actions.map(renderActionButton)}
				{renderActionButton(boxOfficeAction)}
			</Styled.ActionButtonsContainer>
			{/* {isLargeScreen ? (
				<Styled.ActionButtonsContainer>
					{ACTIONS.map((action) => (
						<Styled.ActionButton
							key={action.action}
							onClick={() => handleActionClick(action.action)}
							disabled={action.action === isLoadingAction}
						>
							{isLoadingAction === action.action ? <Loader2 size={12} className="animate-spin" /> : action.icon}
						</Styled.ActionButton>
					))}
				</Styled.ActionButtonsContainer>
			) :

			(
				<Styled.MobileDropdownContainer ref={dropdownRef}>
					<Styled.MobileMenuButton onClick={() => setShowMobileDropdown(!showMobileDropdown)}>
						<MoreVertical size={20} />
					</Styled.MobileMenuButton>

					{showMobileDropdown && (
						<Styled.MobileDropdownMenu>
							{ACTIONS.map((action) => {
								const isDelete = action.action === ActionType.DELETE;

								return (
									<div key={action.action}>
										{isDelete && <Styled.DropdownSeparator />}
										<Styled.MobileActionButton
											onClick={() => {
												handleActionClick(action.action);
												setShowMobileDropdown(false);
											}}
											disabled={action.action === isLoadingAction}
											isDelete={isDelete}
										>
											{isLoadingAction === action.action ? <Loader2 size={16} className="animate-spin" /> : action.icon}
											{action.label}
										</Styled.MobileActionButton>
									</div>
								);
							})}
						</Styled.MobileDropdownMenu>
					)}
				</Styled.MobileDropdownContainer>
			)} */}
		</Styled.Container>
	);
};

const memorizedEventInfo: FC<EventInfoProps> = memo(PunchupEventInfo);

export { memorizedEventInfo as PunchupEventInfo };
